import type { QuizsetFolderCategorySummary } from '@/api/gen/_model/quizsetFolderCategorySummary';
import type { QuizsetFolderItemRes } from '@/api/gen/_model/quizsetFolderItemRes';
import type { QuizsetFolderRes } from '@/api/gen/_model/quizsetFolderRes';
import type { SearchQuizsetFolderRes } from '@/api/gen/_model/searchQuizsetFolderRes';

import type {
  Folder,
  FolderChip,
  FolderItem,
  FolderSummary,
} from '../types/folder.types';

/** 썸네일은 최대 4칸이다. 빈 URL 은 칸을 비워 두는 편이 낫다. */
const toThumbnailUrls = (urls: (string | undefined)[] | undefined): string[] =>
  (urls ?? []).filter((url): url is string => !!url).slice(0, 4);

export const toFolderChips = (
  summary: QuizsetFolderCategorySummary | undefined,
): FolderChip[] => {
  if (!summary) return [];
  const chips: FolderChip[] = [];
  if (summary.school) {
    const { schoolType, schoolGrade, schoolSemester, otherDistinctCount } =
      summary.school;
    chips.push({
      kind: 'SCHOOL',
      schoolType,
      schoolGrade,
      // 서버는 학기가 없으면 null 을 준다. 이걸 undefined 로 바꿔 담으면
      // getServerSideProps 의 직렬화 검사에서 터진다 — Next 는 JSON.stringify 와 달리
      // undefined '값'을 오류로 본다. 그래서 없을 때는 키 자체를 넣지 않는다.
      ...(schoolSemester != null && { schoolSemester }),
      otherCount: otherDistinctCount,
    });
  }
  // 과목 노드를 못 찾았거나 비활성이면 서버가 이름을 빼고 준다 — 그때는 칩을 숨긴다.
  if (summary.subject?.name) {
    const { name, localizeNames, otherDistinctCount } = summary.subject;
    chips.push({
      kind: 'SUBJECT',
      name,
      // 학기와 같은 이유로 없을 때는 키를 넣지 않는다(getServerSideProps 직렬화).
      ...(localizeNames != null && { localizeNames }),
      otherCount: otherDistinctCount,
    });
  }
  if (summary.publisher?.value) {
    chips.push({
      kind: 'PUBLISHER',
      publisherId: summary.publisher.value,
      otherCount: summary.publisher.otherDistinctCount,
    });
  }
  return chips;
};

const toFolderItem = (item: QuizsetFolderItemRes): FolderItem => ({
  quizsetId: item.quizset.id,
  addedAt: item.addedAt,
  quizset: item.quizset,
});

export const toFolder = (res: QuizsetFolderRes): Folder => ({
  id: res.id,
  ownerId: res.ownerId,
  ownerName: res.ownerName,
  ownerAvatarData: res.ownerAvatarData,
  name: res.name,
  visibility: res.visibility,
  quizsetCount: res.quizsetCount,
  thumbnailUrls: toThumbnailUrls(
    res.items?.map(item => item.quizset.templateImageUrl),
  ),
  chips: toFolderChips(res.categorySummary),
  playAction: res.playAction,
  createdAt: res.createdAt,
  updatedAt: res.updatedAt,
  items: res.items?.map(toFolderItem) ?? [],
  ...(res.nextCursor !== undefined && { nextCursor: res.nextCursor }),
});

/**
 * 검색 응답에는 `visibility`·`playAction`·`updatedAt` 이 없다. 지어내지 않고 요약 타입으로 좁힌다 —
 * 그 값이 필요한 화면은 상세 조회(`toFolder`)로 진짜 값을 받는다.
 */
export const toFolderFromSearch = (
  res: SearchQuizsetFolderRes,
): FolderSummary => ({
  id: res.id,
  ownerId: res.ownerId,
  ownerName: res.ownerName,
  name: res.name,
  quizsetCount: res.quizsetCount,
  thumbnailUrls: toThumbnailUrls(res.thumbnailUrls),
  chips: toFolderChips(res.categorySummary),
  createdAt: res.createdAt ?? '',
});
